import { motion } from '@eversports/react-components/design-tokens/motion'
import { Theme } from '@eversports/react-components/design-tokens/theme'
import useMediaQuery from '@eversports/react-components/design-tokens/useMediaQuery'
import useTheme from '@eversports/react-components/design-tokens/useTheme'
import ErrorPage from '@eversports/react-components/patterns/ErrorPage'
import SubscribeToNewsletterButton from '@eversports/react-components/patterns/SubscribeToNewsletterBanner/SubscribeToNewsletterButton'
import BaseLink from '@eversports/react-components/primitives/BaseLink'
import Button from '@eversports/react-components/primitives/Button'
import Flex, { SimpleFlex } from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon from '@eversports/react-components/primitives/Icon'
import Text from '@eversports/react-components/primitives/Text'
import { ClientError } from '@eversports/react-components/client-error'
import { makeStyles } from '@mui/styles'
import React from 'react'

import { Localized } from '../localization/react'

import { InvitationSuccessInvitationQuery } from '../graphql'
import ArrowRightIcon from '../icons/ArrowRightIcon'
import VenueImage from './VenueImage'

type InvitedBy = InvitationSuccessInvitationQuery['invitation']['invitedBy']
type Venue = (InvitedBy & { __typename: 'Venue' })['company']['venues'][number]
interface CompanyDetailsProps {
  venues: Array<Venue> | null
  fetchError?: ClientError
  onSubscribeNewsletter: () => void
  showVenueNewsletterSubscription: boolean
  invitedBy: InvitedBy
}

interface VenueDetailsProps {
  venue: Venue
}

function venueImage(venue: Venue): Venue['images'][number] {
  return venue.images[0] || venue.logo
}

const DESKTOP_VENUE_IMAGE_WIDTH = 200
const DESKTOP_VENUE_IMAGE_HEIGHT = 150
const MOBILE_VENUE_IMAGE_WIDTH = 150
const MOBILE_VENUE_IMAGE_HEIGHT = 112.5
const BRAND_IMAGE_SIZE = 88

const useStyles = makeStyles((theme: Theme) => ({
  venue: {
    marginBottom: theme.layoutSpacing.default,
    maxWidth: DESKTOP_VENUE_IMAGE_WIDTH,
    [theme.breakpoints.down('md')]: {
      maxWidth: MOBILE_VENUE_IMAGE_WIDTH,
    },
  },
  eversportsLogo: {
    width: DESKTOP_VENUE_IMAGE_WIDTH,
    height: DESKTOP_VENUE_IMAGE_HEIGHT,
    [theme.breakpoints.down('md')]: {
      width: MOBILE_VENUE_IMAGE_WIDTH,
      height: MOBILE_VENUE_IMAGE_HEIGHT,
    },
  },
  detailsContainer: {
    maxWidth: 960,
  },
  brandImage: {
    width: BRAND_IMAGE_SIZE,
    height: BRAND_IMAGE_SIZE,
    background: theme.palette.white,
    boxShadow: theme.customShadows.boxShadowDown,
    borderRadius: theme.borderRadii.large,
    border: `${theme.borders.default} ${theme.palette.border}`,
    objectFit: 'cover',
  },
  arrowShadow: {
    filter: 'drop-shadow(0 1px 1px rgba(20, 20, 20, 0.20))',
  },
}))

const venueSection = {
  hidden: { opacity: 0, y: 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      ease: 'easeInOut',
      delay: 0.2,
      duration: 0.2,
    },
  },
}

const container = {
  hidden: { opacity: 0, y: 100 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      ease: 'easeInOut',
      delay: 0.1,
      duration: 0.2,
    },
  },
}

interface BrandDetailsProps {
  image: string | null | undefined
  name: string | null
  to: string
}

function BrandDetails({ image, name, to }: BrandDetailsProps) {
  const classes = useStyles()

  return (
    <Flex flexDirection="column" alignItems="center" verticalSpacing="default">
      <Flex flexDirection="column" alignItems="center" verticalSpacing="dense">
        {image && <img src={image} alt={name ?? undefined} className={classes.brandImage} />}
        <Text textAlign="center" fontWeight="bold">
          {name}
        </Text>
      </Flex>
      <BaseLink to={to} external>
        <Button variant="primary">
          <Flex alignItems="center">
            <Icon
              icon={ArrowRightIcon}
              color="white"
              size="small"
              marginRight="dense"
              className={classes.arrowShadow}
            />
            <Localized id="explore-activities-link" />
          </Flex>
        </Button>
      </BaseLink>
    </Flex>
  )
}

const VenueDetails: React.FC<React.PropsWithChildren<VenueDetailsProps>> = ({ venue }) => {
  const classes = useStyles()
  const theme = useTheme()
  const isInMobileView = useMediaQuery(theme.breakpoints.down('md'))

  const image = venueImage(venue)
  const link = `/widget/w/${venue.shortId}`

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-start"
      verticalSpacing="default"
      marginLeft={isInMobileView ? 'default' : 'wide'}
      marginRight={isInMobileView ? 'default' : 'wide'}
      className={classes.venue}
    >
      <BaseLink to={link} external>
        <VenueImage
          backgroundUrl={image && image.large}
          width={isInMobileView ? MOBILE_VENUE_IMAGE_WIDTH : DESKTOP_VENUE_IMAGE_WIDTH}
          height={isInMobileView ? MOBILE_VENUE_IMAGE_HEIGHT : DESKTOP_VENUE_IMAGE_HEIGHT}
          backgroundSize="cover"
          venueName={venue.name}
          withOverlay
        />
      </BaseLink>
    </Flex>
  )
}

const CompanyDetails: React.FC<React.PropsWithChildren<CompanyDetailsProps>> = ({
  venues,
  fetchError,
  onSubscribeNewsletter,
  showVenueNewsletterSubscription,
  invitedBy,
}) => {
  const classes = useStyles()
  const theme = useTheme()

  return !fetchError ? (
    <motion.div variants={container} initial="hidden" animate="visible">
      <Flex data-testid="company-details" alignItems="center" flexDirection="column">
        <Flex flexDirection="column" className={classes.detailsContainer} verticalSpacing="hero">
          <Heading is="h1" textAlign="center">
            <Localized id="company-details-header" />
          </Heading>
          {showVenueNewsletterSubscription && (
            <Flex flexDirection="column" alignItems="center" verticalSpacing="dense">
              <div>
                <Text textAlign="center" fontWeight="bold">
                  <Localized id="company-newsletter-subscription-title" />
                </Text>
                {invitedBy.__typename === 'Venue' && (
                  <Text textAlign="center">
                    <Localized id="company-newsletter-subscription-text" params={{ venueName: invitedBy.name }} />
                  </Text>
                )}
              </div>
              <SubscribeToNewsletterButton onClick={onSubscribeNewsletter} variant="secondary" />
            </Flex>
          )}
        </Flex>
        {invitedBy.__typename === 'Venue' ? (
          <motion.div
            variants={venueSection}
            style={showVenueNewsletterSubscription ? { marginTop: theme.layoutSpacing.hero } : undefined}
          >
            <SimpleFlex justifyContent="center" flexWrap="wrap">
              {invitedBy.isEligibleForWidgetOverview ? (
                <BrandDetails
                  name={invitedBy.company.widgetBrandSettings.displayName}
                  image={invitedBy.company.widgetBrandSettings.logo?.small}
                  to={`/org/widget/overview?companyId=${invitedBy.company.id}`}
                />
              ) : (
                venues &&
                venues.map((venue) => (
                  <motion.span whileHover={{ scale: 1.1 }} key={venue.id}>
                    <VenueDetails key={venue.name} venue={venue} />
                  </motion.span>
                ))
              )}
            </SimpleFlex>
          </motion.div>
        ) : (
          <BaseLink to={'/'} marginTop="default" external>
            <Button variant="primary">
              <Localized id="family-invitation-go-back" />
            </Button>
          </BaseLink>
        )}
      </Flex>
    </motion.div>
  ) : (
    <ErrorPage error={fetchError} />
  )
}

export default CompanyDetails
